import { useNavigate } from 'react-router-dom';
import { useCallback, useState, useEffect, useRef } from 'react';
import { generateUnifiedCourseRoute, generateUnifiedModuleRoute, mapCategoryToCourseType } from '@/utils/unifiedRouteUtils';
import { getModuleRoute, getCourseRoute } from '@/utils/categoryRouteMapping';
import { PrefetchService } from '@/services/PrefetchService';
import { useNavigationAnalytics } from './useNavigationAnalytics';
import { useLastAccessedModule } from './useLastAccessedModule';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

export const useEnhancedNavigation = () => {
  const navigate = useNavigate();
  const { trackNavigationError, trackRouteGeneration } = useNavigationAnalytics();
  const [isNavigating, setIsNavigating] = useState(false);
  const navigationInProgressRef = useRef(false);

  const safeNavigate = useCallback(async (
    to: string,
    options?: { replace?: boolean; state?: any }
  ) => {
    // Prevent concurrent navigations
    if (navigationInProgressRef.current) {
      logger.warn('Navigation already in progress, ignoring', { to });
      return;
    }

    try {
      navigationInProgressRef.current = true;
      setIsNavigating(true);
      window.scrollTo(0, 0);
      navigate(to, options);
      logger.info('Navigation initiated', { to, options });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Navigation failed';
      trackNavigationError(errorMessage, to);
      toast.error('Navigation failed. Please try again.');
      logger.error('Navigation error', { to, error });
    } finally {
      // Reset flags after a short delay to allow navigation to complete
      setTimeout(() => {
        setIsNavigating(false);
        navigationInProgressRef.current = false;
      }, 100);
    }
  }, [navigate, trackNavigationError]);

  const navigateToCourse = useCallback(async (
    courseId: string, 
    categoryId?: string
  ) => {
    try {
      setIsNavigating(true);
      
      const courseType = categoryId ? mapCategoryToCourseType(categoryId) : undefined;
      const route = generateUnifiedCourseRoute({ 
        courseId, 
        categoryId, 
        courseType 
      });
      
      trackRouteGeneration('course', true, { courseId, categoryId, route });
      await safeNavigate(route);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to navigate to course';
      trackRouteGeneration('course', false, { courseId, categoryId, error: errorMessage });
      toast.error('Failed to navigate to course');
    }
  }, [safeNavigate, trackRouteGeneration]);

  const navigateToModule = useCallback(async (
    courseId: string,
    moduleId: string,
    categoryId?: string,
    updateLastAccessed?: (moduleId: string) => Promise<void>,
    modules?: any[]
  ) => {
    try {
      setIsNavigating(true);
      
      // Update last accessed module if function provided
      if (updateLastAccessed) {
        await updateLastAccessed(moduleId);
      }
      
      // Use centralized route generation with category
      const route = categoryId 
        ? getModuleRoute(courseId, moduleId, categoryId)
        : generateUnifiedModuleRoute({ courseId, moduleId, categoryId });
      
      trackRouteGeneration('module', true, { courseId, moduleId, categoryId, route });
      
      // Prefetch adjacent modules if provided
      if (modules && modules.length > 0) {
        PrefetchService.prefetchAdjacent(courseId, moduleId, modules).catch(err => 
          logger.warn('Prefetch failed:', err)
        );
      }
      
      await safeNavigate(route);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to navigate to module';
      trackRouteGeneration('module', false, { courseId, moduleId, categoryId, error: errorMessage });
      toast.error('Failed to navigate to module');
    }
  }, [safeNavigate, trackRouteGeneration]);

  const navigateBack = useCallback(() => {
    try {
      navigate(-1);
    } catch (error) {
      // Fallback to dashboard if back navigation fails
      safeNavigate('/dashboard');
    }
  }, [navigate, safeNavigate]);

  return {
    safeNavigate,
    navigateToCourse,
    navigateToModule,
    navigateBack,
    isNavigating
  };
};